/**
 * NBA 2K Box Score Parser using Google Vision bounding box coordinates
 * This solves the text-order problem by reconstructing the table spatially
 */

import { normalizeName, isInitialToken, isLastNameToken } from './nameNormalize';

/**
 * Parse NBA 2K box score using coordinate-based approach
 * @param {Array} detections - Individual word detections from Google Vision with bounding boxes
 * @param {Array} players - Database players for name matching
 * @returns {Object} - Parsed player stats
 */
export const parseNBA2KBoxScoreWithCoordinates = (detections, players = []) => {

  if (!detections || detections.length === 0) {
    return { teamName: null, players: [], errors: [] };
  }

  const result = {
    teamName: null,
    players: [],
    errors: []
  };

  // Extract words with their positions
  const words = detections.map(detection => {
    const bounds = detection.boundingPoly.vertices;
    const x = bounds[0].x || 0;
    const y = bounds[0].y || 0;
    const height = (bounds[2].y || 0) - y;
    const width = (bounds[1].x || 0) - x;
    
    return {
      text: detection.description,
      x,
      y,
      width,
      height,
      centerX: x + width / 2,
      centerY: y + height / 2
    };
  });

  // Find player names (they start with initial pattern like "J.")
  // Google Vision returns words separately, so we need to find initials and combine with next word
  const playerRows = [];

  for (let i = 0; i < words.length - 1; i++) {
    const word = words[i];
    const nextWord = words[i + 1];
    
    // Check if this looks like a player initial (e.g., "J.", "P.J.")
    // Nachname mit Diakritika, Binnenversal, Apostroph oder Bindestrich muss
    // mitgehen — sonst faellt die ganze Zeile lautlos aus dem Ergebnis.
    const isInitial = isInitialToken(word.text);
    const isLastName = isLastNameToken(nextWord?.text);
    
    if (isInitial && isLastName && Math.abs(word.centerY - nextWord.centerY) < 10) {
      const playerName = `${word.text} ${nextWord.text}`;
      
      // Find all words on roughly the same horizontal line (same row)
      // These are the stats for this player
      const rowWords = words.filter(w => 
        Math.abs(w.centerY - word.centerY) < 10 && // Same vertical position (±10px)
        w.x > nextWord.x + nextWord.width // To the right of the last name
      );

      // Sort by horizontal position (left to right)
      rowWords.sort((a, b) => a.x - b.x);

      playerRows.push({
        playerName,
        y: word.centerY,
        stats: rowWords.map(w => w.text)
      });

    }
  }


  // Parse each player row
  for (const row of playerRows) {
    try {
      const playerStat = parsePlayerRow(row.playerName, row.stats, players);
      if (playerStat && playerStat.stats) {
        const hasStats = playerStat.stats.minutes > 0 || 
                        playerStat.stats.points > 0 || 
                        playerStat.stats.rebounds > 0;
        
        if (hasStats) {
          result.players.push(playerStat);
        }
      }
    } catch (error) {
      result.errors.push(`${row.playerName}: ${error.message}`);
    }
  }

  return result;
};

/**
 * Parse a single player row
 */
const parsePlayerRow = (playerName, statsArray, players) => {

  // NBA 2K columns: MIN, PTS, REB, AST, STL, BLK, TO, FG, 3PT, FT, OR, FLS
  // We need to identify numbers vs fractions
  
  const stats = {
    minutes: 0,
    points: 0,
    rebounds: 0,
    assists: 0,
    steals: 0,
    blocks: 0,
    turnovers: 0,
    fieldGoalsMade: 0,
    fieldGoalsAttempted: 0,
    threePointersMade: 0,
    threePointersAttempted: 0,
    freeThrowsMade: 0,
    freeThrowsAttempted: 0,
    offensiveRebounds: 0,
    fouls: 0
  };

  // Extract numbers and fractions
  const numbers = [];
  const fractions = [];

  for (const stat of statsArray) {
    // Check for fraction (FG, 3PT, FT)
    const fractionMatch = stat.match(/(\d+)[/-](\d+)/);
    if (fractionMatch) {
      fractions.push({
        made: parseInt(fractionMatch[1]) || 0,
        attempted: parseInt(fractionMatch[2]) || 0,
        raw: stat
      });
    } else if (/^\d+$/.test(stat)) {
      numbers.push(parseInt(stat));
    }
  }


  // Assign numbers to stats (first 7 are: MIN, PTS, REB, AST, STL, BLK, TO)
  if (numbers.length >= 7) {
    stats.minutes = numbers[0];
    stats.points = numbers[1];
    stats.rebounds = numbers[2];
    stats.assists = numbers[3];
    stats.steals = numbers[4];
    stats.blocks = numbers[5];
    stats.turnovers = numbers[6];
    
    // Remaining numbers are OR and FLS
    if (numbers.length > 7) stats.offensiveRebounds = numbers[7];
    if (numbers.length > 8) stats.fouls = numbers[8];
  }

  // Assign fractions (FG, 3PT, FT)
  if (fractions.length >= 1) {
    stats.fieldGoalsMade = fractions[0].made;
    stats.fieldGoalsAttempted = fractions[0].attempted;
  }
  if (fractions.length >= 2) {
    stats.threePointersMade = fractions[1].made;
    stats.threePointersAttempted = fractions[1].attempted;
  }
  if (fractions.length >= 3) {
    stats.freeThrowsMade = fractions[2].made;
    stats.freeThrowsAttempted = fractions[2].attempted;
  }

  // Calculate points from shooting if needed
  const calculatedPoints = 
    (stats.fieldGoalsMade - stats.threePointersMade) * 2 +
    stats.threePointersMade * 3 +
    stats.freeThrowsMade;

  if (calculatedPoints !== stats.points && calculatedPoints > 0) {
    stats.points = calculatedPoints;
  }

  // Match player
  const matchedPlayer = matchPlayerByName(playerName, players);


  return {
    originalName: playerName,
    playerName: matchedPlayer ? `${matchedPlayer.firstName} ${matchedPlayer.lastName}` : playerName,
    playerId: matchedPlayer?.id || null,
    matchConfidence: matchedPlayer?.confidence || 'low',
    stats
  };
};

/**
 * Match OCR player name to database player
 */
const matchPlayerByName = (ocrName, players) => {
  if (!players || players.length === 0) return null;

  const cleanName = normalizeName(ocrName);
  const parts = cleanName.split(/\s+/);

  // Initial + Last name format (e.g., "J Brunson", "PJ Tucker")
  if (parts.length >= 2) {
    const initial = parts[0]; // "j" or "pj"
    const lastName = parts[parts.length - 1]; // "brunson", "tucker"

    // First try: exact first name initial match
    const exactMatches = players.filter(p =>
      normalizeName(p.firstName).startsWith(initial) &&
      normalizeName(p.lastName) === lastName
    );

    if (exactMatches.length === 1) {
      return { ...exactMatches[0], confidence: 'high' };
    }
    
    // If multiple exact matches, try to disambiguate by first letter
    if (exactMatches.length > 1) {
      // For "M.", prefer "Mikal" over "Miles" if OCR shows "M."
      const firstLetterMatches = exactMatches.filter(p =>
        normalizeName(p.firstName)[0] === initial[0]
      );
      
      if (firstLetterMatches.length === 1) {
        return { ...firstLetterMatches[0], confidence: 'high' };
      }
      
      // If still ambiguous, return first match with medium confidence
      return { ...exactMatches[0], confidence: 'medium' };
    }

    // Fuzzy last name match
    const lastNameMatches = players.filter(p =>
      normalizeName(p.lastName).includes(lastName) ||
      lastName.includes(normalizeName(p.lastName))
    );

    if (lastNameMatches.length > 0) {
      return { ...lastNameMatches[0], confidence: 'low' };
    }
  }

  return null;
};
